import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";

import { DataTableNew } from "@/components/shared/DataTable";
import SearchInput from "@/components/shared/SearchInput";
import { Card } from "@/components/ui/Card";

import PermissionGuard from "@/permissions/PermissionGuard";
import { PERMISSIONS } from "@/permissions/constant";

import Skeleton from "@/components/shared/Skeleton";

import { PermissionsService } from "@/services/permissionsService";

import StatusBadge from "@/components/shared/StatusBadge";

import { STATUS } from "@/config/dropdownData";
import {
    Filter,
    FilterPill,
    FilterSelect,
} from "@/components/shared/Filter";
import { formatDate } from "@/utils/dateUtils";

/**
 * The permission catalogue, read only.
 *
 * The codes are seeded by migration and derived from
 * `app/core/permissions.py`, so there is nothing on this screen to write. The
 * Add button, the Actions column, the four modals and the handlers behind them
 * called `permissionsService` mutations that reached no route and reported
 * success anyway; they are gone, along with the service methods.
 */
const Permissions = () => {
    const setHeader = useOutletContext();

    // Search input state
    const [search, setSearch] = useState("");

    // Table metadata (total, loading state)
    const [tableData, setTableData] = useState({
        total: 0,
        isLoading: false,
    });

    // Filter states
    const [statusFilter, setStatusFilter] = useState("");
    const [showFilters, setShowFilters] = useState(false);

    // Set page header dynamically based on table loading state
    useEffect(() => {
        setHeader({
            title: "Permissions List",
            subTitle: tableData.isLoading ? (
                <Skeleton className="h-5 w-28" />
            ) : (
                `Total ${tableData.total} permissions`
            ),
        });

        return () =>
            setHeader({
                title: "",
                subTitle: "",
            });
    }, [tableData, setHeader]);

    /**
     * Table column definitions
     */
    const COLUMNS = [
        {
            key: "displayName",
            sortKey: "display_name",
            label: "Display Name",
            sortable: true,
            align: "left",
            render: (val) => (
                <span className="text-left font-medium text-[13px]">
                    {val ?? "—"}
                </span>
            ),
        },
        {
            key: "category",
            label: "Category",
            sortable: true,
            align: "left",
            render: (val) => (
                <span className="text-left font-medium text-[13px] capitalize">
                    {val ?? "—"}
                </span>
            ),
        },
        {
            key: "isActive",
            sortKey: "is_active",
            label: "Status",
            sortable: true,
            align: "center",
            render: (val) => <StatusBadge isActive={val} />,
        },
        {
            key: "createdAt",
            sortKey: "created_at",
            label: "Created At",
            sortable: true,
            align: "center",
            render: (val) => (
                <span className="text-text-primary text-[12px]">
                    {val ? formatDate(val) : "—"}
                </span>
            ),
        },
    ];

    // Receive table response metadata
    const onDataFetch = (data) => {
        setTableData(data);
    };

    /**
     * Filter config
     */
    const filters = useMemo(
        () => [
            {
                key: "status",
                label: "All Statuses",
                value: statusFilter,
                onChange: setStatusFilter,
                options: STATUS,
            },
        ],
        [statusFilter]
    );

    /**
     * Active filter pills
     */
    const activeFilters = useMemo(() => {
        const selectedStatus = STATUS.find((s) => s.value === statusFilter);

        return [
            statusFilter && {
                key: "status",
                label: `Status: ${selectedStatus?.label}`,
                clear: () => setStatusFilter(""),
            },
        ].filter(Boolean);
    }, [statusFilter]);

    /**
     * Clear filters
     */
    const handleClearAll = () => {
        setStatusFilter("");
    };

    /**
     * API extra params (filters)
     */
    const extraParams = useMemo(
        () => ({
            // TEMPORARY: always list active permissions only. Restore
            // `statusFilter || undefined` with the Filter button below.
            status: true,
        }),
        [statusFilter]
    );

    return (
        <PermissionGuard
            permission={PERMISSIONS.SUPER_ADMIN_MANAGE}
            isComponent
        >
            <div className="flex flex-col gap-2.5" style={{ zoom: 0.85 }}>

                {/* Search + Filters */}
                <div className="flex items-center gap-2.5">
                    <div className="flex-1">
                        <SearchInput
                            value={search}
                            onChange={setSearch}
                            placeholder="Search by name or category..."
                        />
                    </div>

                    {/* TEMPORARY: Filter button hidden — the list shows active permissions only.
                    <Filter
                        filters={filters}
                        activeFilters={activeFilters}
                        showFilters={showFilters}
                        onToggle={() => setShowFilters((p) => !p)}
                        onClearAll={handleClearAll}
                        renderFilter={(f) => (
                            <FilterSelect key={f.key} {...f} />
                        )}
                        renderActiveFilter={(f) => (
                            <FilterPill
                                key={f.key}
                                label={f.label}
                                onRemove={f.clear}
                            />
                        )}
                    />
                    */}
                </div>

                {/* Table */}
                <Card className="p-0 bg-table-heading border-border-primary">
                    <DataTableNew
                        key="permissions-list"
                        className="rounded-none"
                        columns={COLUMNS}
                        showPagination={true}
                        sortable={true}
                        api={PermissionsService.fetchPermissionsList}
                        paginationLabel="permissions"
                        search={search}
                        extraParams={extraParams}
                        dataPath="permissions"
                        onDataFetch={onDataFetch}
                    />
                </Card>
            </div>
        </PermissionGuard>
    );
};

export default Permissions;
